import { useEffect, useMemo, useRef, useState } from "react";

import { PageLayout } from "../components/PageLayout";
import { Button, Card, DropdownField, EmptyState, Spinner, Text, View } from "../components/base";
import { Icon } from "../components/base/Icon";
import { useAppDispatch, useAppSelector } from "../redux";
import { useEffectiveOrganization } from "../hooks/useEffectiveOrganization";
import { agentActions } from "../redux/reducers/agentSlice";
import {
  GetAggregatesParams,
  LlmLabScope,
  LlmLabInputTokenBucket,
  LlmLabOutputTokenBucket,
  LLM_LAB_INPUT_TOKEN_BUCKETS,
  LLM_LAB_OUTPUT_TOKEN_BUCKETS,
  clear,
  getAggregates,
} from "../redux/reducers/llmLabSlice";

type TimeRange = "24h" | "7d" | "30d";

const TIME_RANGE_OPTIONS: { label: string; value: TimeRange }[] = [
  { label: "24h", value: "24h" },
  { label: "7d", value: "7d" },
  { label: "30d", value: "30d" },
];

const SCOPE_ICON: Record<LlmLabScope, string> = {
  global: "language",
  organization: "users",
  agent: "bot",
};

const STREAMING_OPTIONS: { label: string; value: string }[] = [
  { label: "All", value: "" },
  { label: "Streaming", value: "true" },
  { label: "Non-streaming", value: "false" },
];

const INPUT_BUCKET_OPTIONS: readonly LlmLabInputTokenBucket[] = LLM_LAB_INPUT_TOKEN_BUCKETS;
const OUTPUT_BUCKET_OPTIONS: readonly LlmLabOutputTokenBucket[] = LLM_LAB_OUTPUT_TOKEN_BUCKETS;

// Hardcoded for v1 — matches the deployment regions in
// voicerun-completions-proxy/infrastructure/chart/values.*. If we add new
// clusters, add the region here. Misconfigured ("unknown") samples are still
// labeled by the API but aren't exposed as a filter — they show up under
// "All regions" and surface to ops via direct PromQL.
const REGION_OPTIONS: { label: string; value: string }[] = [
  { label: "All regions", value: "" },
  { label: "us-central1", value: "us-central1" },
  { label: "asia-southeast1", value: "asia-southeast1" },
];

// Mirrors voicerun-completions-proxy CompletionsProvider enum. If a new
// provider lands in the proxy, add it here.
const PROVIDER_OPTIONS: { label: string; value: string }[] = [
  { label: "All providers", value: "" },
  { label: "openai", value: "openai" },
  { label: "anthropic", value: "anthropic" },
  { label: "google", value: "google" },
  { label: "anthropic_vertex", value: "anthropic_vertex" },
  { label: "alibaba", value: "alibaba" },
  { label: "deepseek", value: "deepseek" },
];

const RANGE_MS: Record<TimeRange, number> = {
  "24h": 24 * 60 * 60 * 1000,
  "7d": 7 * 24 * 60 * 60 * 1000,
  "30d": 30 * 24 * 60 * 60 * 1000,
};

function getDateRange(range: TimeRange): { startDate: string; endDate: string } {
  const now = new Date();
  return {
    endDate: now.toISOString(),
    startDate: new Date(now.getTime() - RANGE_MS[range]).toISOString(),
  };
}

function formatMs(value: number | null): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  if (value >= 1000) return `${(value / 1000).toFixed(2)}s`;
  return `${Math.round(value)}ms`;
}

function formatCount(value: number | null): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
  return Math.round(value).toString();
}

type SortKey =
  | "model"
  | "responseTimeP50"
  | "responseTimeP95"
  | "ttftP50"
  | "ttftP95"
  | "inputTokensP50"
  | "outputTokensP50"
  | "sampleCount"
  | "streamingSampleCount";

type SortState = { key: SortKey; dir: "asc" | "desc" };

const PillGroup = <T extends string>({
  options,
  value,
  onChange,
}: {
  options: { label: string; value: T; icon?: string }[];
  value: T;
  onChange: (next: T) => void;
}) => (
  <View className="flex flex-row gap-1 flex-wrap">
    {options.map((opt) => {
      const active = opt.value === value;
      return (
        <button
          key={opt.value || "default"}
          onClick={() => onChange(opt.value)}
          className={`flex flex-row items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors cursor-pointer border ${
            active
              ? "bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-800"
              : "text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 border-transparent"
          }`}
        >
          {opt.icon && (
            <Icon
              name={opt.icon}
              size={12}
              className={`shrink-0 ${active ? "text-blue-500 dark:text-blue-400" : "text-gray-400 dark:text-gray-500"}`}
            />
          )}
          {opt.label}
        </button>
      );
    })}
  </View>
);

const FilterSection = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <View className="flex flex-col gap-1.5">
    <Text className="text-[11px] font-medium uppercase tracking-wider text-gray-400 dark:text-gray-500">{label}</Text>
    {children}
  </View>
);

/**
 * MultiSelectField — looks like DropdownField but its popover is a list of
 * checkboxes. Used for token-bucket filters where users want to compose
 * multiple ranges (e.g., "500-1000 + 1000-2000" to get "500-2000").
 *
 * Trigger summarizes the selection: "All sizes" when empty, the single value
 * when one is picked, "N selected" otherwise.
 */
const MultiSelectField = <T extends string>({
  options,
  value,
  onChange,
  emptyLabel = "All",
  className,
}: {
  options: readonly T[];
  value: T[];
  onChange: (next: T[]) => void;
  emptyLabel?: string;
  className?: string;
}) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleMouseDown = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener("mousedown", handleMouseDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleMouseDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  const summary = value.length === 0 ? emptyLabel : value.length === 1 ? value[0] : `${value.length} selected`;

  const toggle = (opt: T) => {
    if (value.includes(opt)) onChange(value.filter((v) => v !== opt));
    else onChange([...value, opt]);
  };

  return (
    <View
      ref={containerRef as any}
      className={`relative ${className ?? ""}`}
    >
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex flex-row items-center justify-between w-full h-[38px] px-3 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-white cursor-pointer hover:border-gray-300 dark:hover:border-gray-600 transition-colors"
      >
        <span className="truncate">{summary}</span>
        <Icon
          name={open ? "chevron-up" : "chevron-down"}
          size={12}
          className="text-gray-400 dark:text-gray-500 shrink-0 ml-2"
        />
      </button>
      {open && (
        <View className="absolute left-0 right-0 top-full mt-1 z-30 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-2xl py-1">
          {value.length > 0 && (
            <button
              type="button"
              onClick={() => onChange([])}
              className="block w-full text-left px-3 py-1.5 text-[12px] text-blue-500 dark:text-blue-400 hover:bg-gray-100 dark:hover:bg-gray-800/60 cursor-pointer"
            >
              Clear all
            </button>
          )}
          {options.map((opt) => {
            const selected = value.includes(opt);
            return (
              <button
                key={opt}
                type="button"
                onClick={() => toggle(opt)}
                className="flex flex-row items-center gap-2 w-full px-3 py-2 text-sm text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-800/60 cursor-pointer"
              >
                <View
                  className={`flex items-center justify-center w-4 h-4 rounded border ${
                    selected ? "bg-blue-500 border-blue-500" : "bg-transparent border-gray-300 dark:border-gray-600"
                  }`}
                >
                  {selected && (
                    <Icon
                      name="check"
                      size={12}
                      className="text-white"
                    />
                  )}
                </View>
                <span>{opt}</span>
              </button>
            );
          })}
        </View>
      )}
    </View>
  );
};

const SortHeader = ({
  label,
  sortKey,
  sort,
  onSort,
  align = "right",
}: {
  label: string;
  sortKey: SortKey;
  sort: SortState;
  onSort: (key: SortKey) => void;
  align?: "left" | "right";
}) => {
  const active = sort.key === sortKey;
  return (
    <button
      type="button"
      onClick={() => onSort(sortKey)}
      className={`flex flex-row items-center gap-1 cursor-pointer transition-colors ${
        align === "right" ? "justify-end" : "justify-start"
      } ${active ? "text-gray-700 dark:text-gray-200" : "text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"}`}
    >
      <span>{label}</span>
      <Icon
        name={active ? (sort.dir === "asc" ? "chevron-up" : "chevron-down") : "chevron-down"}
        size={10}
        className={active ? "" : "opacity-0"}
      />
    </button>
  );
};

type AgentOption = { id: string; name: string; description: string };

type ScopeSelection = { kind: "global" } | { kind: "organization" } | { kind: "agent"; agentId: string };

const MenuRow = ({
  iconName,
  label,
  sublabel,
  monoLabel = false,
  selected,
  onClick,
}: {
  iconName: string;
  label: string;
  sublabel?: string;
  monoLabel?: boolean;
  selected: boolean;
  onClick: () => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    className={`flex flex-row items-center gap-2.5 w-full px-2.5 py-2 rounded-md text-left transition-colors cursor-pointer ${
      selected ? "bg-blue-500/10 dark:bg-blue-500/15" : "hover:bg-gray-100 dark:hover:bg-gray-800/60"
    }`}
  >
    <View
      className={`flex items-center justify-center w-[22px] h-[22px] rounded shrink-0 ${
        selected ? "bg-blue-500/20 text-blue-500 dark:text-blue-300" : "bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400"
      }`}
    >
      <Icon
        name={iconName}
        size={12}
      />
    </View>
    <View className="flex-1 min-w-0">
      <Text className={`block text-[13.5px] font-medium text-gray-900 dark:text-white truncate ${monoLabel ? "font-mono text-[13px]" : ""}`}>{label}</Text>
      {sublabel && <Text className="block text-[11.5px] text-gray-500 dark:text-gray-500 truncate mt-px">{sublabel}</Text>}
    </View>
    {selected && (
      <Icon
        name="check"
        size={14}
        className="text-blue-500 dark:text-blue-400 shrink-0"
      />
    )}
  </button>
);

const ScopeMenu = ({
  scope,
  agentId,
  agentOptions,
  agentsLoading,
  orgName,
  onChange,
}: {
  scope: LlmLabScope;
  agentId: string;
  agentOptions: AgentOption[];
  agentsLoading: boolean;
  orgName: string;
  onChange: (next: ScopeSelection) => void;
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  // Close on outside click and Escape; restore focus to the trigger when
  // closed via Escape so keyboard users don't get dropped on document.body.
  useEffect(() => {
    if (!open) return;
    const handleMouseDown = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
        setSearch("");
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
        setSearch("");
        triggerRef.current?.focus();
      }
    };
    document.addEventListener("mousedown", handleMouseDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleMouseDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  // Autofocus the search field after open paints.
  useEffect(() => {
    if (open) {
      const id = window.requestAnimationFrame(() => searchRef.current?.focus());
      return () => window.cancelAnimationFrame(id);
    }
  }, [open]);

  const selectedAgent = useMemo(() => (scope === "agent" ? agentOptions.find((a) => a.id === agentId) : undefined), [scope, agentId, agentOptions]);

  let primaryLabel: string;
  let secondaryLabel: string;
  let primaryIsMono = false;
  if (scope === "global") {
    primaryLabel = "Global";
    secondaryLabel = "Across VoiceRun";
  } else if (scope === "organization") {
    primaryLabel = "Organization";
    secondaryLabel = orgName || "Current organization";
  } else if (selectedAgent) {
    primaryLabel = selectedAgent.name;
    secondaryLabel = "Single agent";
    primaryIsMono = true;
  } else {
    primaryLabel = "Pick an agent";
    secondaryLabel = "Single agent";
  }

  const filteredAgents = useMemo(() => {
    if (!search) return agentOptions;
    const q = search.toLowerCase();
    return agentOptions.filter((a) => a.name.toLowerCase().includes(q));
  }, [agentOptions, search]);

  const select = (next: ScopeSelection) => {
    onChange(next);
    setOpen(false);
    setSearch("");
  };

  return (
    <View
      className="relative"
      ref={containerRef as any}
    >
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex flex-row items-center gap-2.5 w-full h-11 px-3 rounded-lg cursor-pointer text-left bg-blue-500/10 hover:bg-blue-500/15 dark:bg-blue-500/10 dark:hover:bg-blue-500/20 border border-blue-500 transition-colors"
      >
        <View className="flex items-center justify-center w-7 h-7 rounded-md shrink-0 bg-blue-500/20 text-blue-500 dark:text-blue-300">
          <Icon
            name={SCOPE_ICON[scope]}
            size={14}
          />
        </View>
        <View className="flex-1 min-w-0">
          <Text className={`block text-sm font-medium text-gray-900 dark:text-white truncate ${primaryIsMono ? "font-mono" : ""}`}>{primaryLabel}</Text>
          <Text className="block text-[11.5px] text-gray-500 dark:text-gray-500 truncate">{secondaryLabel}</Text>
        </View>
        <Icon
          name={open ? "chevron-up" : "chevron-down"}
          size={12}
          className="text-gray-400 dark:text-gray-500 shrink-0"
        />
      </button>

      {open && (
        <View className="absolute left-0 right-0 top-full mt-1.5 z-30 rounded-[10px] border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-2xl p-2 pt-2.5">
          <MenuRow
            iconName="language"
            label="Global"
            sublabel="Across VoiceRun"
            selected={scope === "global"}
            onClick={() => select({ kind: "global" })}
          />
          <MenuRow
            iconName="users"
            label="Organization"
            sublabel={orgName || "Current organization"}
            selected={scope === "organization"}
            onClick={() => select({ kind: "organization" })}
          />

          <View className="mx-2 mt-3 pt-3 pb-2 border-t border-gray-200 dark:border-gray-700/60 flex flex-row items-center gap-2">
            <Text className="text-[10.5px] font-semibold uppercase tracking-[0.08em] text-gray-500 dark:text-gray-500 shrink-0">My Agents</Text>
            <View className="flex-1 flex flex-row items-center gap-1.5 h-6 border border-gray-200 dark:border-gray-700/60 rounded-md px-2 text-gray-500 dark:text-gray-400">
              <Icon
                name="search"
                size={11}
              />
              <input
                ref={searchRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search…"
                className="flex-1 min-w-0 bg-transparent border-0 outline-none p-0 m-0 text-[11px] leading-none text-gray-900 dark:text-white placeholder:text-[11px] placeholder:text-gray-400 dark:placeholder:text-gray-500"
              />
            </View>
          </View>

          <View className="max-h-[200px] overflow-y-auto mt-1">
            {agentsLoading && agentOptions.length === 0 && (
              <View className="flex flex-col gap-1 px-2 py-2">
                {[0, 1, 2].map((i) => (
                  <View
                    key={i}
                    className="h-9 rounded bg-gray-100 dark:bg-gray-800/60 animate-pulse"
                  />
                ))}
              </View>
            )}
            {!agentsLoading && agentOptions.length === 0 && (
              <View className="flex flex-col items-start gap-1 px-3 py-3">
                <Text className="text-[13px] text-gray-500 dark:text-gray-400">No agents in this org yet.</Text>
                <a
                  href="/agents"
                  className="text-[12px] text-blue-500 dark:text-blue-400 hover:underline"
                >
                  Create one →
                </a>
              </View>
            )}
            {!agentsLoading && agentOptions.length > 0 && filteredAgents.length === 0 && (
              <Text className="block text-[13px] text-gray-500 dark:text-gray-500 px-3.5 py-3.5">No matches</Text>
            )}
            {filteredAgents.map((agent) => (
              <MenuRow
                key={agent.id}
                iconName="bot"
                label={agent.name}
                sublabel={agent.description || "Agent"}
                monoLabel
                selected={scope === "agent" && agentId === agent.id}
                onClick={() => select({ kind: "agent", agentId: agent.id })}
              />
            ))}
          </View>
        </View>
      )}
    </View>
  );
};

export const LLMLabPage = () => {
  const dispatch = useAppDispatch();
  const { aggregates, loading, error } = useAppSelector((state) => state.llmLab);
  const agentEntities = useAppSelector((state) => state.agent.entities);
  const agentsLoading = useAppSelector((state) => state.agent.isLoading);
  const { organization } = useEffectiveOrganization();

  const [timeRange, setTimeRange] = useState<TimeRange>("7d");
  const [scope, setScope] = useState<LlmLabScope>("global");
  const [streaming, setStreaming] = useState<"" | "true" | "false">("");
  const [inputBuckets, setInputBuckets] = useState<LlmLabInputTokenBucket[]>([]);
  const [outputBuckets, setOutputBuckets] = useState<LlmLabOutputTokenBucket[]>([]);
  const [region, setRegion] = useState<string>("");
  const [provider, setProvider] = useState<string>("");
  const [agentId, setAgentId] = useState<string>("");
  const [sort, setSort] = useState<SortState>({ key: "sampleCount", dir: "desc" });

  const handleSort = (key: SortKey) => {
    setSort((prev) => {
      if (prev.key === key) return { key, dir: prev.dir === "asc" ? "desc" : "asc" };
      // Text columns default to ascending; numeric to descending (most users
      // want the highest latency / sample count surfaced first).
      return { key, dir: key === "model" ? "asc" : "desc" };
    });
  };

  const sortedAggregates = useMemo(() => {
    const arr = [...aggregates];
    arr.sort((a, b) => {
      const av = a[sort.key];
      const bv = b[sort.key];
      // Nulls always sort to the bottom regardless of direction so missing
      // data doesn't dominate the top of the table.
      const aNull = av === null || av === undefined;
      const bNull = bv === null || bv === undefined;
      if (aNull && bNull) return 0;
      if (aNull) return 1;
      if (bNull) return -1;
      if (typeof av === "string" && typeof bv === "string") {
        return sort.dir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
      }
      const an = av as number;
      const bn = bv as number;
      return sort.dir === "asc" ? an - bn : bn - an;
    });
    return arr;
  }, [aggregates, sort]);

  // Pre-fetch the org's agents once so the scope menu populates instantly.
  useEffect(() => {
    dispatch(agentActions.get({}));
  }, [dispatch]);

  const agentOptions = useMemo<AgentOption[]>(
    () =>
      Object.values(agentEntities)
        .map((agent: any) => ({ id: agent.id as string, name: agent.name as string, description: (agent.description as string) || "" }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [agentEntities],
  );

  const fetchParams = useMemo<GetAggregatesParams | null>(() => {
    // Defer the fetch until the user has chosen an agent for the agent scope —
    // otherwise the API rejects scope=agent without an agentId.
    if (scope === "agent" && !agentId) return null;
    const { startDate, endDate } = getDateRange(timeRange);
    const params: GetAggregatesParams = { startDate, endDate, scope };
    if (scope === "agent") params.agentId = agentId;
    if (streaming === "true" || streaming === "false") params.streaming = streaming;
    if (inputBuckets.length > 0) params.inputTokenBuckets = inputBuckets;
    if (outputBuckets.length > 0) params.outputTokenBuckets = outputBuckets;
    if (region) params.region = region;
    if (provider) params.provider = provider;
    return params;
  }, [timeRange, scope, agentId, streaming, inputBuckets, outputBuckets, region, provider]);

  useEffect(() => {
    if (!fetchParams) {
      dispatch(clear());
      return;
    }
    dispatch(getAggregates(fetchParams));
    return () => {
      dispatch(clear());
    };
  }, [dispatch, fetchParams]);

  const totalSamples = aggregates.reduce((sum, agg) => sum + agg.sampleCount, 0);
  const needsAgentPick = scope === "agent" && !agentId;

  return (
    <PageLayout
      title="LLM Lab"
      description="Compare latency across LLMs. Aggregates are sourced from primfunctions.completions traces over the selected time range."
    >
      <View className="flex flex-col gap-4 mt-6">
        <Card className="p-4">
          <View className="flex flex-col gap-4">
            <View className="flex flex-row flex-wrap gap-x-8 gap-y-4">
              <FilterSection label="Scope">
                <View className="w-72">
                  <ScopeMenu
                    scope={scope}
                    agentId={agentId}
                    agentOptions={agentOptions}
                    agentsLoading={agentsLoading}
                    orgName={organization?.name || ""}
                    onChange={(next) => {
                      if (next.kind === "agent") {
                        setScope("agent");
                        setAgentId(next.agentId);
                      } else {
                        setScope(next.kind);
                        setAgentId("");
                      }
                    }}
                  />
                </View>
              </FilterSection>
              <FilterSection label="Region">
                <View className="w-44">
                  <DropdownField
                    value={region}
                    onChange={(v) => setRegion(v ?? "")}
                    options={REGION_OPTIONS}
                  />
                </View>
              </FilterSection>
              <FilterSection label="Provider">
                <View className="w-44">
                  <DropdownField
                    value={provider}
                    onChange={(v) => setProvider(v ?? "")}
                    options={PROVIDER_OPTIONS}
                  />
                </View>
              </FilterSection>
              <FilterSection label="Time range">
                <PillGroup
                  options={TIME_RANGE_OPTIONS}
                  value={timeRange}
                  onChange={setTimeRange}
                />
              </FilterSection>
            </View>
            <View className="flex flex-row flex-wrap gap-4">
              <FilterSection label="Streaming">
                <View className="w-44">
                  <DropdownField
                    value={streaming}
                    onChange={(v) => setStreaming((v as "" | "true" | "false") ?? "")}
                    options={STREAMING_OPTIONS}
                  />
                </View>
              </FilterSection>
              <FilterSection label="Input tokens">
                <MultiSelectField
                  className="w-44"
                  options={INPUT_BUCKET_OPTIONS}
                  value={inputBuckets}
                  onChange={setInputBuckets}
                  emptyLabel="All sizes"
                />
              </FilterSection>
              <FilterSection label="Output tokens">
                <MultiSelectField
                  className="w-44"
                  options={OUTPUT_BUCKET_OPTIONS}
                  value={outputBuckets}
                  onChange={setOutputBuckets}
                  emptyLabel="All sizes"
                />
              </FilterSection>
            </View>
            {!loading && !error && !needsAgentPick && (
              <Text className="text-xs text-gray-400 dark:text-gray-500">
                {aggregates.length} model{aggregates.length === 1 ? "" : "s"} · {formatCount(totalSamples)} sample
                {totalSamples === 1 ? "" : "s"}
              </Text>
            )}
          </View>
        </Card>

        {needsAgentPick && (
          <EmptyState
            icon="bot"
            title="Pick an agent"
            description="Choose an agent in your organization above to see its LLM latency aggregates."
          />
        )}

        {!needsAgentPick && loading && (
          <View className="flex items-center justify-center h-64">
            <Spinner size={24} />
          </View>
        )}

        {!needsAgentPick && !loading && error && (
          <View className="flex flex-col items-center justify-center h-64 gap-3">
            <Icon
              name="error"
              size={32}
              className="text-red-400"
            />
            <Text className="text-sm text-red-500">{error}</Text>
            <Button
              variant="neutral"
              size="auto"
              onClick={() => fetchParams && dispatch(getAggregates(fetchParams))}
            >
              Retry
            </Button>
          </View>
        )}

        {!needsAgentPick && !loading && !error && aggregates.length === 0 && (
          <EmptyState
            icon="bar_chart"
            title="No LLM data for this slice"
            description="Try a wider time range, fewer filters, or switch scope. Data is captured as completions flow through the platform."
          />
        )}

        {!needsAgentPick && !loading && !error && aggregates.length > 0 && (
          <Card className="p-0 overflow-hidden">
            <View className="grid grid-cols-[1.6fr_repeat(4,minmax(0,1fr))_repeat(2,minmax(0,1.4fr))_repeat(2,minmax(0,0.9fr))] gap-0 text-[10.5px] font-medium uppercase tracking-wider text-gray-400 dark:text-gray-500 px-4 py-3 border-b border-gray-100 dark:border-gray-800">
              <SortHeader
                label="Model"
                sortKey="model"
                sort={sort}
                onSort={handleSort}
                align="left"
              />
              <SortHeader
                label="Resp p50"
                sortKey="responseTimeP50"
                sort={sort}
                onSort={handleSort}
              />
              <SortHeader
                label="Resp p95"
                sortKey="responseTimeP95"
                sort={sort}
                onSort={handleSort}
              />
              <SortHeader
                label="TTFT p50"
                sortKey="ttftP50"
                sort={sort}
                onSort={handleSort}
              />
              <SortHeader
                label="TTFT p95"
                sortKey="ttftP95"
                sort={sort}
                onSort={handleSort}
              />
              <SortHeader
                label="Input tokens p50"
                sortKey="inputTokensP50"
                sort={sort}
                onSort={handleSort}
              />
              <SortHeader
                label="Output tokens p50"
                sortKey="outputTokensP50"
                sort={sort}
                onSort={handleSort}
              />
              <SortHeader
                label="Samples"
                sortKey="sampleCount"
                sort={sort}
                onSort={handleSort}
              />
              <SortHeader
                label="Streaming"
                sortKey="streamingSampleCount"
                sort={sort}
                onSort={handleSort}
              />
            </View>
            {sortedAggregates.map((agg) => (
              <View
                key={`${agg.provider}|${agg.model}`}
                className="grid grid-cols-[1.6fr_repeat(4,minmax(0,1fr))_repeat(2,minmax(0,1.4fr))_repeat(2,minmax(0,0.9fr))] gap-0 px-4 py-3 border-b border-gray-100 dark:border-gray-800 last:border-b-0 items-center"
              >
                <View className="flex flex-col min-w-0">
                  <Text className="text-sm font-medium text-gray-900 dark:text-white truncate">{agg.model}</Text>
                  <Text className="text-[11px] text-gray-500 dark:text-gray-400 truncate">{agg.provider}</Text>
                </View>
                <Text className="text-sm text-right tabular-nums text-gray-900 dark:text-white">{formatMs(agg.responseTimeP50)}</Text>
                <Text className="text-sm text-right tabular-nums text-gray-900 dark:text-white">{formatMs(agg.responseTimeP95)}</Text>
                <Text className="text-sm text-right tabular-nums text-gray-900 dark:text-white">{formatMs(agg.ttftP50)}</Text>
                <Text className="text-sm text-right tabular-nums text-gray-900 dark:text-white">{formatMs(agg.ttftP95)}</Text>
                <Text className="text-sm text-right tabular-nums text-gray-900 dark:text-white">{formatCount(agg.inputTokensP50)}</Text>
                <Text className="text-sm text-right tabular-nums text-gray-900 dark:text-white">{formatCount(agg.outputTokensP50)}</Text>
                <Text className="text-sm text-right tabular-nums text-gray-900 dark:text-white">{formatCount(agg.sampleCount)}</Text>
                <Text className="text-sm text-right tabular-nums text-gray-900 dark:text-white">{formatCount(agg.streamingSampleCount)}</Text>
              </View>
            ))}
          </Card>
        )}
      </View>
    </PageLayout>
  );
};
